import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import { _U } from "../../../modules/utils";
import "./LiveCaptionOverlay.css";

// B1 (G14): Console JW Player TypeA/B/E `.react-jw-player-container` 하위에 정적 마운트되는
// 운영자 시점 라이브 자막 오버레이. Default 템플릿은 첫 릴리즈 제외(G13).
//
// 프레임 수신: Socket.js → window message "SOCKET-RECEIVED" envelope를 직접 구독한다
// (ControlPanel.shared.js onReceivedMessage와 동일 패턴). 콘솔 WS 연결 자체가 세션 scope이므로
// eventIdx/sessionIdx props 미제공 시 서버 측 scope를 신뢰하고, 제공 시 재검증한다.
//
// 표시 정책: final은 ring buffer(G25, 3문장 중 최신 표시), partial은 운영자 전용
// 미리보기 라인(G7 — 청중 미송출, 운영자 시점에서만 보임). mode!==auto 또는
// fail_closed면 전체 숨김. G30: body.modal-open 동안 visibility:hidden + aria-hidden
// + pointer-events:none.

const CAPTION_ACTIONS = ["caption:state", "caption:partial", "caption:final", "caption:correction"];
const FINAL_RING_BUFFER_LIMIT = 3; // G25
const FADE_OUT_MS = 150; // G25
const INTERNAL_POC_STT_STORAGE_KEY = "LIVEE_INTERNAL_POC_STT";

const isCaptionFrame = (frame = {}) => CAPTION_ACTIONS.includes(frame.action);

const isLiveCaptionOverlayEnabled = () => {
  if (
    typeof process !== "undefined" &&
    process.env &&
    process.env.LIVEE_INTERNAL_POC_STT === "true"
  ) {
    return true;
  }
  if (typeof window === "undefined") {
    return false;
  }

  try {
    if (window.localStorage?.getItem(INTERNAL_POC_STT_STORAGE_KEY) === "true") {
      return true;
    }
  } catch {
    // Ignore storage failures and keep the overlay disabled by default.
  }

  const query = `${window.location?.search || ""}${window.location?.hash || ""}`;
  return query.includes("sttPoc=1") || query.includes("captionPoc=1");
};

const isSameCaptionScope = ({ payload = {}, eventIdx, sessionIdx }) => {
  if (!eventIdx && !sessionIdx) {
    // fail-safe: scope 미지정이면 차단한다. 마운트 측(JWPlayer)이 consoleCfg의
    // eventIdx/sessionIdx를 반드시 전달해야 하며, 다른 세션 frame 표시를 구조적으로 막는다.
    return false;
  }
  return (
    String(payload.eventIdx || "") === String(eventIdx || "") &&
    String(payload.sessionIdx || "") === String(sessionIdx || "")
  );
};

export const shouldShowConsoleLiveCaption = (runtime = {}) =>
  runtime.mode === "auto" && runtime.status === "running";

export default function LiveCaptionOverlay({ eventIdx, sessionIdx }) {
  const overlayEnabled = isLiveCaptionOverlayEnabled();
  const runtimeRef = useRef({ mode: "off", status: "idle" });
  const closeTimerRef = useRef(null);
  const [runtime, setRuntime] = useState(runtimeRef.current);
  const [finalSegments, setFinalSegments] = useState([]);
  const [partialText, setPartialText] = useState("");
  const [modalHidden, setModalHidden] = useState(false);
  const [closing, setClosing] = useState(false);
  const [mounted, setMounted] = useState(false);

  // G30: 모달 활성 시 자동 숨김 — Bootstrap 계열 모달이 body.modal-open을 사용한다
  useEffect(() => {
    if (!overlayEnabled) {
      return undefined;
    }

    const syncModalState = () => setModalHidden(document.body.classList.contains("modal-open"));
    syncModalState();
    const observer = new MutationObserver(syncModalState);
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, [overlayEnabled]);

  useEffect(() => {
    if (!overlayEnabled) {
      return undefined;
    }

    const clearCaptionContent = () => {
      setFinalSegments([]);
      setPartialText("");
    };

    const updateRuntime = (payload = {}) => {
      const nextRuntime = {
        mode: payload.mode || runtimeRef.current.mode || "off",
        status: payload.status || runtimeRef.current.status || "idle",
      };
      runtimeRef.current = nextRuntime;
      setRuntime(nextRuntime);
      if (!shouldShowConsoleLiveCaption(nextRuntime) || nextRuntime.status === "fail_closed") {
        clearCaptionContent();
      }
      return nextRuntime;
    };

    const upsertFinalSegment = (payload = {}) => {
      if (!payload.text) {
        return;
      }
      setFinalSegments((prevSegments) => {
        const segmentId = String(payload.segmentId || payload.captionSegmentId || Date.now());
        const nextSegment = { id: segmentId, text: String(payload.text) };
        const filtered = prevSegments.filter((segment) => segment.id !== nextSegment.id);
        return [nextSegment, ...filtered].slice(0, FINAL_RING_BUFFER_LIMIT);
      });
    };

    const handleCaptionFrame = (frame = {}) => {
      if (!isCaptionFrame(frame)) {
        return false;
      }
      const payload = frame.data || {};
      if (!isSameCaptionScope({ payload, eventIdx, sessionIdx })) {
        return true;
      }
      if (frame.action === "caption:state") {
        updateRuntime(payload);
        return true;
      }
      if (frame.action === "caption:partial") {
        if (shouldShowConsoleLiveCaption(runtimeRef.current)) {
          setPartialText(payload.text ? String(payload.text) : "");
        }
        return true;
      }
      // caption:final / caption:correction
      const nextRuntime = updateRuntime(payload);
      if (shouldShowConsoleLiveCaption(nextRuntime)) {
        upsertFinalSegment(payload);
        setPartialText("");
      }
      return true;
    };

    const onReceivedMessage = (e) => {
      _U.parseMessage(e)
        .then((data) => {
          switch (data.code) {
            case "SOCKET-RECEIVED":
              return data.data ? JSON.parse(data.data) : null;
            default:
              return null;
          }
        })
        .then((frame) => {
          if (frame) {
            handleCaptionFrame(frame);
          }
        })
        .catch(() => {});
    };

    // 패널의 clearLiveCaptionOverlay()가 React 관리 DOM을 직접 비우지 않고
    // 이 이벤트로 React state를 통해 정리하게 한다(reconciliation 충돌 방지)
    const onClearRequested = () => clearCaptionContent();

    window.addEventListener("message", onReceivedMessage);
    window.addEventListener("livee:live-caption-overlay-clear", onClearRequested);
    return () => {
      window.removeEventListener("message", onReceivedMessage);
      window.removeEventListener("livee:live-caption-overlay-clear", onClearRequested);
    };
  }, [eventIdx, overlayEnabled, sessionIdx]);

  const visible =
    shouldShowConsoleLiveCaption(runtime) && (finalSegments.length > 0 || partialText.length > 0);

  // G25: 표시 → 숨김 전환 시 150ms fade-out 후 unmount
  useEffect(() => {
    if (visible) {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      setClosing(false);
      setMounted(true);
      return undefined;
    }
    if (!mounted) {
      return undefined;
    }
    setClosing(true);
    closeTimerRef.current = setTimeout(() => {
      setMounted(false);
      setClosing(false);
      closeTimerRef.current = null;
    }, FADE_OUT_MS);
    return () => {
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
  }, [visible, mounted]);

  if (!overlayEnabled || !mounted) {
    return null;
  }

  const latestSegment = finalSegments[0];

  return (
    <div
      aria-hidden={modalHidden}
      aria-live="polite"
      className={cx("console_live_caption_overlay", { "modal-hidden": modalHidden, closing })}
      data-live-caption-overlay
      role="status"
    >
      {latestSegment ? (
        <p className="console_live_caption_final" key={latestSegment.id}>
          {latestSegment.text}
        </p>
      ) : null}
      {partialText ? <p className="console_live_caption_partial">{partialText}</p> : null}
    </div>
  );
}
